<template>
	<div class="access-control-container">
		<r-pop-menu
			class="permission-selection"
			:closeOnContentPointerDown="true"
			containerWidth="300px"
			:popActivatorStyleOverride="{
				width: `100%`,
			}"
			:disabled="permissionDropdownDisabled"
		>
			<div 
				slot="activator"
				class="permission-selection-activator"
				:class="{
					'disabled': permissionDropdownDisabled,
				}"
			>
				<div class="user-label">
					{{ user.displayLabel }}
				</div>
				<div 
					class="permission-selection-activator-selector"
				>
					<div 
						class="permission-selection-activator-text"
						:class="{
							'disabled': permissionDropdownDisabled,
						}"
					>
						{{ currentAccessLevelLabel }}
					</div>
					<r-standard-icon
						:class="{
							'icon-invisible': permissionDropdownDisabled,
						}"
						icon="smallArrowDown"
						color="grey-300"
						:size="{
							height: 6,
							width: 9,
						}"
					>
					</r-standard-icon>
				</div>
			</div>
			<r-pop-list
				slot="content"
			>
				<r-pop-list-item 
					class="permission-selection-item"
					v-for="permissionLevel in permissionLevels"
					:key="permissionLevel.key"
					:enabled="canSetPermission(permissionLevel.permission)"
					@pointerdown="setAccessLevel(permissionLevel)"
				>
					<div class="possible-checkmark">
						<r-standard-icon
							class="checkmark-icon"
							v-if="permissionLevel.key === currentAccessLevel"
							icon="checkMark"
						></r-standard-icon>
					</div>
					<div class="permission-item-container">
						<div class="permission-label">{{ permissionLevel.label }}</div>
						<div class="permission-description">{{ permissionLevel.description }}</div>
						<div class="permission-icon-wrapper">
							<p-icon overrideColor :icon="!permissionLevel.hasEntitlement ? 'upgrade' : 'blank'" />
						</div>
					</div>
				</r-pop-list-item>
				<div class="divider full-width">
					<div class="divider-inner"></div>
				</div>
				<r-pop-list-item 
					class="permission-selection-item remove"
					:enabled="canRemovePermission()"
					@pointerdown="removeAccessLevel"
				>
					<div class="permission-item-container">
						<div class="permission-label">Remove</div>
						<div class="permission-description">
							This user will revert to the same level of access as non-invited users in {{workspaceName}}.
						</div>
					</div>
				</r-pop-list-item>
			</r-pop-list>
		</r-pop-menu>
	</div>
</template>

<script>

import CloudStore from 'js/cloudstore/cloudstore';

export default {
	name: 'access-control-user-item',
	data(){
		return{
			selectedPermission: null,
		};
	},
	$pubsub: {},
	props:{
		dataStore:{
			required: true,
			type: Object,
		},
		user:{
			required: true,
			type: Object,
		},
		permissionLicenses: {
			required: false
		}
	},
	computed:{
		userGUID(){
			return this.user.userGUID;
		},
		currentUserGUID(){
			if(this.$root.systemState.auth.userDetails){
				return this.$root.systemState.auth.userDetails.user_guid;
			}
			return null;
		},
		isCurrentUser(){
			return this.userGUID === this.currentUserGUID;
		},
		permissionDropdownDisabled(){
			return this.isCurrentUser || !this.canRemovePermission();
		},
		currentAccessLevelLabel(){
			const labelMap = {
				viewer: "View",
				commenter: "Comment",
				editor: "Edit",
				owner: "Owner",
			};

			if(this.currentAccessLevel){
				return labelMap[this.currentAccessLevel];
			}
			else{
				return "";
			}
		},
		currentAccessLevel(){
			if(this.dataStore.accessControl && this.dataStore.accessControl[this.userGUID] && this.dataStore.accessControl[this.userGUID].level){
				return this.dataStore.accessControl[this.userGUID].level;
			}
			else{
				return null;
			}
		},
		permissionLevels(){
			return [{
				key: 'viewer',
				label: 'View access',
				hasEntitlement: this.permissionLicenses.viewer,
				description: 'This user can view this Workbook, but cannot add or edit any data.',
				permission: 'access_addViewer',
			},{
				key: 'commenter',
				label: 'Comment access',
				hasEntitlement: this.permissionLicenses.commenter,
				description: 'This user can view this Workbook and add comments but cannot edit any data.',
				permission: 'access_addCommenter',
			},{ 
				key: 'editor',
				label: 'Edit access',
				hasEntitlement: this.permissionLicenses.editor,
				description: 'This user can view this Workbook and add or edit it’s data.',
				permission: 'access_addEditor',
			},{
				key: 'owner',
				label: 'Owner access',
				hasEntitlement: this.permissionLicenses.owner,
				description: 'This user can edit this Workbook and it’s user permissions.',
				permission: 'access_addOwner',
			}];
		},
		removePermissions(){
			return {
				viewer: 'access_removeViewer',
				commenter: 'access_removeCommenter',
				editor: 'access_removeEditor',
				owner: 'access_removeOwner',
			};
		},
		workspaceName(){
			return this.$root.systemState.auth.currentWorkspaceLabel;
		},
	},
	methods:{
		workbookHasPermission(accessRequestKey){
			if(this.dataStore){
				return this.dataStore.hasPermission(accessRequestKey);
			}
			else{
				Assert.throwError('Tried to get permission before workbook loaded');
			}
		},
		canSetPermission(permission){
			if(this.currentAccessLevel){
				return this.workbookHasPermission(this.removePermissions[this.currentAccessLevel]) && this.workbookHasPermission(permission);
			}
			else{
				return false;
			}
		},
		canRemovePermission(){
			if(this.currentAccessLevel){
				return this.workbookHasPermission(this.removePermissions[this.currentAccessLevel]);
			}
			else{
				return false;
			}
		},
		setAccessLevel(permissionObj){
			// check remove then add
			if (permissionObj.hasEntitlement) {
				if(this.canSetPermission(permissionObj.permission)){
					this.dataStore.setUserPermission(this.userGUID, permissionObj.key);
				}
			} else {
				this.$pubsub.modules.upgrade.pub("openModal", {
					flowName: "entitlementExhausted",
					attributes: {
						entitlementIdentifier: {
							owner: "Workbook.Roles.Owner",
							editor: "Workbook.Roles.Editor",
							commenter: "Workbook.Roles.Commenter",
							viewer: "Workbook.Roles.Viewer"
						}[permissionObj.key]
					}
				});
			}

			return;
		},
		removeAccessLevel(){
			if(this.canRemovePermission()){
				this.dataStore.removeUserPermission(this.userGUID);
			}
			return;
		},
	},
}

</script>

<style scoped lang="scss">

.access-control-container{
	@include flex(row);

	width: 100%;
	padding: 10px 0;

	.permission-selection{
		width: 100%;
		cursor: pointer;

		.permission-selection-activator{
			@include flex(row, space-between);
			width: 100%;

			&.disabled{
				cursor: default;
			}

			.user-label{
				font-family: $rvFontFamily;
				font-size: 14px;
				line-height: 20px;
				letter-spacing: 0.01em;
				color: #2C2C38;
				max-width: 230px;
				overflow-x: hidden;
				text-overflow: ellipsis;
			}

			.permission-selection-activator-selector{
				@include flex(row);

				.permission-selection-activator-text{
					font-family: $rvFontFamily;
					font-size: 14px;
					line-height: 20px;
					text-align: right;
					letter-spacing: 0.01em;
					color: $p-color-grey-400;
					margin-right: 6px;

					&.disabled{
						color: $p-color-grey-300;
					}
				}

				.icon-invisible{
					opacity: 0;
				}
			}
		}

		.permission-selection-item{
			height: 79px;

			.possible-checkmark{
				width: 39px;
				padding-right: 9px;
			}

			.permission-item-container{
				@include flex(column, flex-start, flex-start);
				position: relative;

				.permission-label{
					font-family: $rvFontFamily;
					font-size: 14px;
					line-height: 20px;
					letter-spacing: 0.01em;
					color: $p-color-grey-600;
				}
				.permission-description{
					font-family: $rvFontFamily;
					font-size: 13px;
					line-height: 19px;
					letter-spacing: 0.01em;
					color: $p-color-grey-400;
				}
				.permission-icon-wrapper {
					position: absolute;
					top: 0px;
					right: 0px;
					color: $p-color-yellow-300;
					width: 20px;
					height: 20px;
				}
			}

			&.remove{
				height: 98px;
			}
		}
	}

}

.divider{
	padding: 9px 0;

	.divider-inner{
		height: 1px;
		background: $p-color-grey-200;
	}

	&.full-width{
		width: calc(100% - 18px);
		margin: auto;
		position: relative;
	}
}
</style>
